import React, { useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import TripDateSelector from "./trip-component/trip-date-selector";
import RangePicker from "./trip-component/range-picker";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from '@mui/icons-material/Menu';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { colors } from "../constants/colors";
import { logOutFromServer } from "../redux/actions/auth-action";
import { toast } from "react-toastify";
import { setIsLoggedIn } from "../redux/reducers/auth-reducer";
import { ActionTypes } from "../redux/type";
import { endUserSession } from "../utils/analyticsTracker";
import { useNavigate } from "react-router-dom";
import "./header.css";

interface HeaderProps {
  fleetData: any;
  playingStatus: boolean;
  profileDetails: string;
  selectedDate: any;
  availableDates: any[];
  onDateChange: (date: Dayjs) => void;
  onToggle: () => void;
  isCollapsed: boolean;
  onMenuClick: () => void;
  disableDateChange?: boolean;
  dateRange?: { start: string; end: string } | null;
  onRangeChange?: (range: { start: string; end: string }) => void;
}

const tabStyle = (on: boolean): React.CSSProperties => ({
  flex: 1,
  height: 28,
  border: "none",
  borderRadius: 6,
  background: on ? "#ED6926" : "transparent",
  color: on ? "#fff" : "#374151",
  fontSize: 12,
  cursor: "pointer",
});

const dateLabel = (selectedDate: any, dateRange?: { start: string; end: string } | null) =>
  dateRange
    ? `${dayjs(dateRange.start).format("MMM D")} – ${dayjs(dateRange.end).format("MMM D, YYYY")}`
    : dayjs(selectedDate).format("MMM D, YYYY");

const Header: React.FC<HeaderProps> = React.memo(({
  fleetData,
  playingStatus,
  profileDetails,
  selectedDate,
  availableDates,
  onDateChange,
  onToggle,
  isCollapsed,
  onMenuClick,
  disableDateChange = false,
  dateRange = null,
  onRangeChange,
}) => {
  const [openDate, setOpenDate] = useState(false);
  const [pickMode, setPickMode] = useState<"day" | "range">(dateRange ? "range" : "day");
  // The day calendar closes itself on any outside click, the Day tab included;
  // a new key on each Day click remounts it open.
  const [dayKey, setDayKey] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userDetails = useSelector((state: any) => state?.auth?.loginDetails);
  const [menuOpen, setMenuOpen] = useState(false);
  const allState = useSelector((state: any) => state);
  const dispatch = useDispatch();
  const Navigate = useNavigate();

  // Leave at once; the server call can take long when the API is busy.
  const handleLogout = () => {
    const details = allState?.auth?.loginDetails?.user_details?.[0];
    logOutFromServer({ refresh_token: details?.refresh_token }, details?.access_token);
    localStorage.removeItem("loginAt");
    dispatch(setIsLoggedIn(false));
    dispatch({ type: ActionTypes.GLOBAL_RESET, payload: undefined });
    endUserSession(userDetails);
    Navigate("/");
    toast.success('Logout Successfully', {
      position: "top-right",
      autoClose: 3000,
      theme: "light"
    });
  };

  const handleToggleDateDropdown = () => {
    if (disableDateChange) return;
    if (!openDate) setPickMode(dateRange ? "range" : "day");
    setOpenDate((prev) => !prev);
  };

  // Day or Range tabs over the calendar. A range shows graph and snapshots
  // for up to 20 days; video stays a single-day thing.
  const renderDropdown = (showToolbar: boolean) => (
    <div className="date-dropdown">
      {onRangeChange && (
        <div style={{ display: "flex", gap: 4, padding: 4, background: "#f3f4f6", borderRadius: 8, marginBottom: 4 }}>
          <button type="button" style={tabStyle(pickMode === "day")} onClick={() => { setPickMode("day"); setDayKey((k) => k + 1); }}>Day</button>
          <button type="button" style={tabStyle(pickMode === "range")} onClick={() => setPickMode("range")}>Range (max 20 days)</button>
        </div>
      )}
      {pickMode === "range" && onRangeChange ? (
        <RangePicker
          availableDates={availableDates}
          range={dateRange}
          onApply={(r) => {
            onRangeChange(r);
            setOpenDate(false);
          }}
        />
      ) : (
        // Desktop: the calendar is absolutely placed at its wrapper's right
        // edge, so give it a right-aligned box under the tabs to sit in.
        <div style={showToolbar ? undefined : { display: "flex", justifyContent: "flex-end", minWidth: 250, minHeight: 250 }}>
          <TripDateSelector
            key={dayKey}
            {...(showToolbar ? {} : { showToolbar: false })}
            tripDate={dayjs(selectedDate)}
            availableDates={availableDates}
            // In a range, clicking the highlighted day must still leave the range.
            allowSameDay={!!dateRange}
            onChangeDate={(date) => {
              onDateChange(date);
              setOpenDate(false);
            }}
            disabled={disableDateChange}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="header">
      <div className="header-left">
        <MenuIcon className="phoneMenu"
          onClick={() => {
            onMenuClick();
          }} />
        <img src="/logo.svg" className="logo" alt="logo" />
        <img
          src="/toggle.svg"
          className="toggle"
          alt="toggle"
          onClick={onToggle}
        />
      </div>

      <div className="header-right">
        <div className="group mobile-hidden">
          <div className="date-wrapper">
            <div
              className="date-filter"
              onClick={handleToggleDateDropdown}
              style={disableDateChange ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" } : undefined}
            >
              <span>{dateLabel(selectedDate, dateRange)}</span>
              {openDate ? <ArrowDropUpIcon className="arrow" /> : <ArrowDropDownIcon className="arrow" />}
            </div>
            {openDate && renderDropdown(false)}
          </div>
        </div>

        <div className="hideOnDesktop">
          <div className="date-wrapper">
            <div
              className="date-filter"
              onClick={handleToggleDateDropdown}
              style={disableDateChange ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" } : undefined}
            >
              <span>{dateLabel(selectedDate, dateRange)}</span>
              {openDate ? <ArrowDropUpIcon className="arrow" /> : <ArrowDropDownIcon className="arrow" />}
            </div>
            {openDate && renderDropdown(true)}
          </div>
        </div>

        <div className="status mobile-hidden">
          <div className="divider-line"></div>
          <div className="live">
            Live <span className="count orange">{fleetData?.total_online || 0}</span>
          </div>
          <div className="offline">
            Offline <span className="count dark">{fleetData?.total_offline || 0}</span>
          </div>
          <div className="divider-line"></div>
        </div>

        {/* Profile Section for mobile view */}
        <div className="profile-container">
          <div className="dropdown" onClick={() => setMenuOpen(!menuOpen)}>
            <span
              className="profile-name"
              title={userDetails?.user_details[0]?.username}
            >
              {userDetails?.user_details[0]?.username?.slice(0, 2).toUpperCase()}
            </span>

            <ArrowDropDownIcon
              style={{ fontSize: "16px" }}
              className={menuOpen ? "rotate" : ""}
            />
          </div>

          {menuOpen && (
            <div className="desktop-dropdown-menu">
              <div
                onClick={handleLogout}
                className="btn btn-dark btn-sm"
              >
                <ExitToAppIcon style={{ color: colors.white, fontSize: "20px" }} />
                Logout
              </div>
            </div>
          )}

          {mobileMenuOpen && (
            <div className="mobile-dropdown-menu">
              {playingStatus ? (
                <button className="playing-btn">▶&nbsp;Playing</button>
              ) : (
                <button className="stop-btn">⏸&nbsp;Pause</button>
              )}
              <div className="mobile-menu-item">
                <div className="live">
                  Live <span className="count orange">{fleetData?.total_online || 0}</span>
                </div>
                <hr />
                <div className="offline">
                  Offline <span className="count dark">{fleetData?.total_offline || 0}</span>
                </div>
              </div>
              <div
                onClick={handleLogout}
                className="btn btn-dark btn-sm"
                style={{ zIndex: 999 }}
              >
                <div>
                  <ExitToAppIcon style={{ color: colors.white, fontSize: "23px" }} /> Logout
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default Header;

// import React, { useState } from "react";
// import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
// import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
// import TripDateSelector from "./trip-component/trip-date-selector";
// import dayjs, { Dayjs } from "dayjs";
// import { useDispatch, useSelector } from "react-redux";
// import MenuIcon from '@mui/icons-material/Menu';
// import ExitToAppIcon from '@mui/icons-material/ExitToApp';
// import { colors } from "../constants/colors";
// import { logOutFromServer } from "../redux/actions/auth-action";
// import { toast } from "react-toastify";
// import { setIsLoggedIn } from "../redux/reducers/auth-reducer";
// import { ActionTypes } from "../redux/type";
// import { endUserSession } from "../utils/analyticsTracker";
// import { useNavigate } from "react-router-dom";
// import "./header.css";

// interface HeaderProps {
//   fleetData: any;
//   playingStatus: boolean;
//   profileDetails: string;
//   selectedDate: any;
//   availableDates: any[];
//   onDateChange: (date: Dayjs) => void;
//   onToggle: () => void;
//   isCollapsed: boolean;
//   onMenuClick: () => void;
//   disableDateChange?: boolean;
// }

// const Header: React.FC<HeaderProps> = React.memo(({
//   fleetData,
//   playingStatus,
//   profileDetails,
//   selectedDate,
//   availableDates,
//   onDateChange,
//   onToggle,
//   isCollapsed,
//   onMenuClick,
//   disableDateChange = false,
// }) => {
//   const [openDate, setOpenDate] = useState(false);
//   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
//   const userDetails = useSelector((state: any) => state?.auth?.loginDetails);
//   const [menuOpen, setMenuOpen] = useState(false);
//   const allState = useSelector((state: any) => state);
//   const dispatch = useDispatch();
//   const Navigate = useNavigate();

//   const handleLogout = async () => {
//     const logoutRes: any = await logOutFromServer({
//       refresh_token: allState?.auth.loginDetails.user_details[0].refresh_token
//     });
//     if (logoutRes) {
//       toast.success('Logout Successfully', {
//         position: "top-right",
//         autoClose: 9000,
//         theme: "light"
//       });
//     }
//     dispatch(setIsLoggedIn(false));
//     dispatch({ type: ActionTypes.GLOBAL_RESET, payload: undefined });
//     endUserSession(userDetails);
//     Navigate("/");
//   };

//   const handleToggleDateDropdown = () => {
//     if (disableDateChange) return;
//     setOpenDate((prev) => !prev);
//   };

//   return (
//     <div className="header">
//       <div className="header-left">
//         <MenuIcon className="phoneMenu"
//           onClick={() => {
//             onMenuClick();
//           }} />
//         <img src="/logo.svg" className="logo" alt="logo" />
//         <img
//           src="/toggle.svg"
//           className="toggle"
//           alt="toggle"
//           onClick={onToggle}
//         />
//       </div>

//       <div className="header-right">
//         <div className="group mobile-hidden">
//           <div className="date-wrapper">
//             <div
//               className="date-filter"
//               onClick={handleToggleDateDropdown}
//               style={disableDateChange ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" } : undefined}
//             >
//               <span>{dayjs(selectedDate).format("MMM D, YYYY")}</span>
//               {openDate ? <ArrowDropUpIcon className="arrow" /> : <ArrowDropDownIcon className="arrow" />}
//             </div>
//             {openDate && (
//               <div className="date-dropdown">
//                 <TripDateSelector
//                   tripDate={dayjs(selectedDate)}
//                   availableDates={availableDates}
//                   onChangeDate={(date) => {
//                     onDateChange(date);
//                     setOpenDate(false);
//                   }}
//                   disabled={disableDateChange}
//                 />
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="hideOnDesktop">
//           <div className="date-wrapper">
//             <div
//               className="date-filter"
//               onClick={handleToggleDateDropdown}
//               style={disableDateChange ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" } : undefined}
//             >
//               <span>{dayjs(selectedDate).format("MMM D, YYYY")}</span>
//               {openDate ? <ArrowDropUpIcon className="arrow" /> : <ArrowDropDownIcon className="arrow" />}
//             </div>
//             {openDate && (
//               <div className="date-dropdown">
//                 <TripDateSelector
//                   tripDate={dayjs(selectedDate)}
//                   availableDates={availableDates}
//                   onChangeDate={(date) => {
//                     onDateChange(date);
//                     setOpenDate(false);
//                   }}
//                   disabled={disableDateChange}
//                 />
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="status mobile-hidden">
//           <div className="divider-line"></div>
//           <div className="live">
//             Live <span className="count orange">{fleetData?.total_online || 0}</span>
//           </div>
//           <div className="offline">
//             Offline <span className="count dark">{fleetData?.total_offline || 0}</span>
//           </div>
//           <div className="divider-line"></div>
//         </div>

//         {/* Profile Section for mobile view */}
//         <div className="profile-container">
//           <div className="dropdown" onClick={() => setMenuOpen(!menuOpen)}>
//             <span
//               className="profile-name"
//               title={userDetails?.user_details[0]?.username}
//             >
//               {userDetails?.user_details[0]?.username?.slice(0, 2).toUpperCase()}
//             </span>

//             <ArrowDropDownIcon
//               style={{ fontSize: "16px" }}
//               className={menuOpen ? "rotate" : ""}
//             />
//           </div>

//           {menuOpen && (
//             <div className="desktop-dropdown-menu">
//               <div
//                 onClick={handleLogout}
//                 className="btn btn-dark btn-sm"
//               >
//                 <ExitToAppIcon style={{ color: colors.white, fontSize: "20px" }} />
//                 Logout
//               </div>
//             </div>
//           )}

//           {mobileMenuOpen && (
//             <div className="mobile-dropdown-menu">
//               {playingStatus ? (
//                 <button className="playing-btn">▶&nbsp;Playing</button>
//               ) : (
//                 <button className="stop-btn">⏸&nbsp;Pause</button>
//               )}
//               <div className="mobile-menu-item">
//                 <div className="live">
//                   Live <span className="count orange">{fleetData?.total_online || 0}</span>
//                 </div>
//                 <hr />
//                 <div className="offline">
//                   Offline <span className="count dark">{fleetData?.total_offline || 0}</span>
//                 </div>
//               </div>
//               <div
//                 onClick={handleLogout}
//                 className="btn btn-dark btn-sm"
//                 style={{ zIndex: 999 }}
//               >
//                 <div>
//                   <ExitToAppIcon style={{ color: colors.white, fontSize: "23px" }} /> Logout
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// });

// export default Header;